<template>
  <div class="chart-wrapper">
    <div class="chart-background">
      <template v-if="isLoading">
        <div style="text-align: center; padding-top: 10px; padding-bottom: 10px">
          <span class="fa fa-2x fa-spinner fa-spin" />
        </div>
      </template>
      <template v-else-if="subscribables.length === 0">
        <div style="text-align: center; padding-top: 10px; padding-bottom: 10px">
          <p>{{ trans('subscription.no_subscribables_found') }}</p>
        </div>
      </template>
      <template v-else>
        <div class="subscribe">
          <button
            :disabled="selectedSubscribables.length === 0"
            class="btn btn-sm btn-default subscribe__button"
            @click="isSubscribing = true"
          >
            {{ trans('subscription.subscribe_students') }}
          </button>

          <button
            class="btn btn-sm btn-default subscribe__button"
            @click="toggleSelection = !toggleSelection"
          >
            {{ trans('subscription.werkplekken.reset_selections') }}
          </button>
        </div>
        <ApexTimelineCharts
          :series="series"
          :options="chartOptions"
          @selection-changed="selectionChanged"
        />
      </template>
    </div>
    <Modal
      :open="isSubscribing"
      :title="trans('subscription.subscribe_students')"
      @close="isSubscribing = false"
    >
      <div class="row">
        <input-wrapper
          id="options"
          :label="trans('subscription.werkplekken.selected_options')"
        >
          <h5><strong>{{ selectedDetail }}</strong></h5>
          <div
            v-for="classroom in Object.keys(selectedSubscribablesList)"
            :key="'list-' + classroom"
          >
            {{ classroom }}
            <ul>
              <li
                v-for="name in selectedSubscribablesList[classroom]"
                :key="'list-' + classroom + '-' + name"
              >
                {{ name }}
              </li>
            </ul>
          </div>
        </input-wrapper>

        <input-wrapper
          id="can_unsubscribe"
          :label="trans('subscription.can_unsubscribe')"
        >
          <input
            id="canUnsubscribeCheckbox"
            v-model="canUnsubscribe"
            type="checkbox"
          >
        </input-wrapper>

        <multi-select-component
          id="studentSelector"
          v-model="students"
          style="margin-top:10px;"
          :max-selected-options="500"
          :label="trans_choice('definitions.students', 2)"
          :errormessage="errors['students'] != null ? errors['students'][0] : null"
          :search-at-mounted="true"
          :include="{ 'program_id': programId }"
          type="student"
          class="row"
        />

        <input-wrapper
          v-if="needsConfirmation"
          id="confirmed"
          :label="trans('core.confirm')"
          :errormessage="errors['confirmed'] != null ? errors['confirmed'][0] : null"
        >
          <input
            id="confirmedCheckbox"
            v-model="confirmed"
            type="checkbox"
          >
        </input-wrapper>

        <div
          class="pull-right"
          style="margin-top:20px"
        >
          <button
            class="btn btn-default "
            :disabled="isSaving"
            @click="isSubscribing = false"
          >
            {{ trans('core.cancel') }}
          </button>
          <button
            class="btn btn-primary"
            :disabled="isSaving"
            @click="storeSubscriptions"
          >
            {{ trans('core.save') }}
          </button>
        </div>
        <br style="clear: both">
      </div>
    </Modal>
  </div>
</template>

<script>
import ApexTimelineCharts from '@/shared/Charts/ApexCharts/ApexTimelineCharts'
import MultiSelectComponent from '@/employees/components/FormComponents/MultiSelectComponent'
import { mapState } from 'vuex'
import moment from 'moment'
import Modal from '@/employees/AttendanceList/components/Modal'
import toast from '@/services/toast'
import InputWrapper from '@/employees/components/FormComponents/InputWrapper.vue'

export default {
  name: 'EmployeeDayCalendarPerClassroom',
  components: { InputWrapper, Modal, MultiSelectComponent, ApexTimelineCharts },
  props: {
    subscribables: {
      // required includes: events.classrooms
      type: Array,
      required: true,
    },
    storeRoute: {
      type: String,
      required: true,
    },
    isLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
    classroomSelected: {
      type: Boolean,
      required: false,
      default: false,
    },
    programId: {
      type: String,
      required: true,
    },
  },

  data () {
    return {
      selectedSubscribables: [],
      isSubscribing: false,
      isSaving: false,
      needsConfirmation: false,

      students: [],
      canUnsubscribe: false,
      confirmed: false,
      errors: {},
      toggleSelection: false,
    }
  },

  computed: {
    ...mapState({
      series (state) {
        const date = moment().startOf('date')
        return [
          {
            data: this.subscribables.map(subscribable => {
              const event = state.events.events[subscribable.event]
              const classroom = state.classrooms.classrooms[event.classrooms[0]]
              const mySubscription = null

              const openForSubscription = !(moment(subscribable.startDate) > moment()) &&
                  !(moment(subscribable.deadlineDate) < moment())
              const hasCapacity = subscribable.placesAvailable === null || subscribable.placesAvailable > 0

              const subscribed = false
              const canSubscribe = !subscribed &&
                  !(subscribable.status === 'complied') &&
                  openForSubscription &&
                  hasCapacity &&
                  !(subscribable.status === 'subscribedToOther')

              const color = (subscribed ? '#4dbd74' : (canSubscribe ? '#25167a' : '#777777'))
              const subscribeState = (subscribed ? 'subscribed' : (canSubscribe ? 'canSubscribe' : 'cannotSubscribe'))

              const xvalue = this.classroomSelected ? moment(event.starttime).format('YYYY-MM-DD') : classroom.name

              // We need to change the start to the same time
              const eventDate = moment(event.starttime).startOf('date')
              const diff = eventDate.diff(date, 'seconds')
              const students = subscribable.subscriptions.map(subscriptionId => state.subscribables.subscriptions[subscriptionId]).map(subscription => state.subscribables.students[subscription.student].name)

              return {
                x: xvalue,
                y: [moment(event.starttime).unix() - diff, moment(event.endtime).unix() - diff],
                strokeColor: color,
                fillColor: color,
                data: {
                  state: subscribeState,
                  subscribable,
                  mySubscription,
                  event,
                  classroom,
                  students,
                },
              }
            }),
          },
        ]
      },
      selectedSubscribablesList (state) {
        const subscribables = this.selectedSubscribables.map(subscribable => {
          const event = state.events.events[subscribable.event]
          subscribable.classroom = state.classrooms.classrooms[event.classrooms[0]]
          return subscribable
        })

        if (this.classroomSelected) {
          // group the subscribables by day
          return subscribables.reduce((acc, subscribable) => {
            const event = state.events.events[subscribable.event]
            const date = moment(event.starttime, 'YYYY-MM-DD').format('ddd DD/MM')
            if (!acc.hasOwnProperty(date)) {
              acc[date] = []
            }

            const placesPostfix = subscribable.placesAvailable !== null ? ' (' + Math.max(0, subscribable.placesAvailable) + ')' : ''

            acc[date].push(subscribable.name + placesPostfix)
            return acc
          }, {})
        } else {
          // group the subscribables by classroom
          return this.selectedSubscribables.map(subscribable => {
            const event = state.events.events[subscribable.event]
            subscribable.classroom = state.classrooms.classrooms[event.classrooms[0]]

            return subscribable
          }).reduce((acc, subscribable) => {
            const classroom = subscribable.classroom.name
            if (acc[classroom] == null) {
              acc[classroom] = []
            }

            const placesPostfix = subscribable.placesAvailable !== null ? ' (' + Math.max(0, subscribable.placesAvailable) + ')' : ''

            acc[classroom].push(subscribable.name + placesPostfix)
            return acc
          }, {})
        }
      },
      selectedDetail (state) {
        const firstSubscribable = this.selectedSubscribables[0]

        if (firstSubscribable == null) {
          return ''
        }

        if (!this.classroomSelected) {
          const event = state.events.events[firstSubscribable.event]
          return moment(event.starttime, 'YYYY-MM-DD').format('ddd DD/MM')
        }

        const event = state.events.events[firstSubscribable.event]
        return state.classrooms.classrooms[event.classrooms[0]].name
      },
    }),

    height () {
      const classrooms = [...new Set(this.series[0].data.map(item => item.x))]
      return (classrooms.length * 30) + 100 + 'px'
    },

    minTime () {
      // Get the earlyest starttime
      let min = Number.MAX_SAFE_INTEGER
      this.series[0].data.forEach(function (item) {
        if (item.y[0] < min) {
          min = item.y[0]
        }
      })

      // round down to the hour for clean ticks
      return moment.unix(min).startOf('hour').unix()
    },

    maxTime () {
      // Get the latest endtime
      let max = 0
      this.series[0].data.forEach(function (item) {
        if (item.y[1] > max) {
          max = item.y[1]
        }
      })

      // round up to the hour for clean ticks
      const m = moment.unix(max)
      const roundUp = m.minute() || m.second() || m.millisecond()
        ? m.add(1, 'hour').startOf('hour')
        : m.startOf('hour')

      return roundUp.unix()
    },

    hourTicks () {
      const start = moment.unix(this.minTime)
      const end = moment.unix(this.maxTime)

      const duration = moment.duration(end.diff(start))
      return duration.asHours()
    },

    chartOptions () {
      return {
        chart: {
          type: 'rangeBar',
          height: this.height,
          width: '100%',
          // width: '888px',
          toolbar: {
            show: false,
            tools: {
              download: false,
              selection: false,
              zoom: false,
              zoomin: false,
              zoomout: false,
              pan: false,
            },
          },
          zoom: {
            enabled: false,
          },
          animations: {
            enabled: false,
          },
        },
        states: {
          active: {
            allowMultipleDataPointsSelection: true,
            filter: {
              type: 'lighten',
              value: 0.4,
            },
          },
        },
        grid: {
          padding: {
            right: 20,
            left: 20,
          },
        },
        plotOptions: {
          bar: {
            horizontal: true,
          },
          dataLabels: {
            enabled: true,
          },

        },
        legend: {
          show: true,
          position: 'top',
          showForSingleSeries: true,
          customLegendItems: [
            Lang.get('subscription.werkplekken.not_available_for_student') + ' (# ' + Lang.get('core.seats').toLowerCase() + ')',
            Lang.get('subscription.werkplekken.available_for_student') + ' (# ' + Lang.get('core.seats').toLowerCase() + ')'],
          markers: {
            fillColors: ['#777777', '#25167a'],
          },
          itemMargin: {
            horizontal: 10,
            vertical: 10,
          },
        },
        dataLabels: {
          enabled: true,
          formatter: function (value, { seriesIndex, dataPointIndex, w }) {
            const point = w.config.series[seriesIndex].data[dataPointIndex]
            return point.data.subscribable.placesAvailable !== null ? Math.max(0, point.data.subscribable.placesAvailable) : ''
          },
        },
        tooltip: {
          x: {
            show: false,
            formatter: function (value) {
              return moment.unix(value).format('HH')
            },
          },
          custom: function({series, seriesIndex, dataPointIndex, w}) {
              let data = '<div class="arrow_box"><span>' +w.config.series[seriesIndex].data[dataPointIndex].data.subscribable.name + '</span></br>'
              if(w.config.series[seriesIndex].data[dataPointIndex].data.students.length > 0){
                data += '<span>'+ w.config.series[seriesIndex].data[dataPointIndex].data.students.join('</br>')+'</span></div>'
              }

              return data
          },
        },
        noData: {
          text: Lang.get('subscription.no_subscribables_found'),
        },
        xaxis: {
          type: 'datetime',
          min: this.minTime,
          max: this.maxTime,
          position: 'top',
          tickAmount: this.hourTicks,
          labels: {
            formatter: function (value) {
              return moment.unix(value).format('HH')
            },
          },
        },
        yaxis: {
          labels: {
            formatter: (value) => {
              if (this.classroomSelected) {
                return moment(value, 'YYYY-MM-DD').format('ddd DD/MM')
              }
              return value
            },
          },
        },
        toggleSelection: this.toggleSelection,
      }
    },
  },

  methods: {
    selectionChanged (points) {
      this.selectedSubscribables = points.map(point => point.data.subscribable)
    },

    storeSubscriptions () {
      const subscribableIds = this.selectedSubscribables.map(subscribable => subscribable.id)
      const studentIds = this.students.map(student => student.id)

      this.isSaving = true
      this.errors = {}
      this.$store.dispatch('storeSubscriptionsWerkplekken', {
        apiRoute: this.storeRoute,
        data: {
          subscribables: subscribableIds,
          students: studentIds,
          can_unsubscribe: this.canUnsubscribe,
          confirmed: this.confirmed,
        },
      }).then(() => {
        toast.success(Lang.get('subscription.students_subscribed'))
        this.isSubscribing = false
        this.students = []
        this.canUnsubscribe = false
        this.needsConfirmation = false
        this.confirmed = false
        this.toggleSelection = !this.toggleSelection
      }).catch((error) => {
        if (error.response.status === 422) {
          this.errors = error.response.data

          if (this.errors.confirmed != null) {
            this.needsConfirmation = true
          }
        }
        toast.error(Lang.get('subscription.no_students_subscribed'))
      }).finally(() => {
        this.isSaving = false
      })
    },
  },
}
</script>

<style scoped lang="scss">
.subscribe {
  display: flex;
  padding: 0px 10px 0px 10px;

  &__button {
    width: 100%;
    border: 1px solid rgba(37, 22, 122, 0.85);
  }
}

:deep(.arrow_box) {
  position: relative;
  background: white;
  color:black;
  border: 1px solid #000000;
}
:deep(.arrow_box:after, .arrow_box:before) {
  right: 100%;
  top: 50%;
  border: solid transparent;
  content: " ";
  height: 0;
  width: 0;
  position: absolute;
  pointer-events: none;
}

:deep(.arrow_box:after) {
  border-color: rgba(85, 85, 85, 0);
  border-right-color: #555;
  border-width: 10px;
  margin-top: -10px;
}
:deep(.arrow_box:before) {
  border-color: rgba(0, 0, 0, 0);
  border-right-color: #000000;
  border-width: 13px;
  margin-top: -13px;
}

:deep(.apexcharts-tooltip) {
  color: black;
  transform: translateX(10px) translateY(10px);
  overflow: visible !important;
  white-space: normal !important;
}

:deep(.apexcharts-tooltip span) {
  padding: 5px 10px;
  display: inline-block;
}

</style>
